import React, { useState, useEffect, useMemo } from 'react';
import { Container, Navbar, Nav, NavDropdown, Form, Row, Col, Popover, Button, OverlayTrigger } from 'react-bootstrap';
import { pega_ranking_soma_bolsa, pega_matriz_curricular } from "../../services/api";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Registrando os componentes necessários do Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Mapa para definir a ordem correta das séries, alinhado com a saída da função convertSerie
const serieSortOrder = new Map([
    ['Mat 1', 1], ['Mat 2', 2], ['Pré 1', 3], ['Pré 2', 4],
    ['1º Fund I', 5], ['2º Fund I', 6], ['3º Fund I', 7], ['4º Fund I', 8], ['5º Fund I', 9],
    ['6º Fund II', 10], ['7º Fund II', 11], ['8º Fund II', 12], ['9º Fund II', 13],
    ['1ª Médio', 14], ['2ª Médio', 15], ['3ª Médio', 16],
]);


// Função para converter o código "gra-ser" para o nome da série
const convertSerie = (seg) => {
    switch (seg) {
        case "0-1": return "Mat 1";
        case "0-2": return "Mat 2";
        case "0-3": return "Pré 1";
        case "0-4": return "Pré 2";
        case "1-1": return "1º Fund I";
        case "1-2": return "2º Fund I";
        case "1-3": return "3º Fund I";
        case "1-4": return "4º Fund I";
        case "1-5": return "5º Fund I";
        case "1-6": return "6º Fund II";
        case "1-7": return "7º Fund II";
        case "1-8": return "8º Fund II";
        case "1-9": return "9º Fund II";
        case "2-1": return "1ª Médio";
        case "2-2": return "2ª Médio";
        case "2-3": return "3ª Médio";
        default: return seg;
    }
};


const DesempenhoPorSerie = ({ un }) => {
    const [data2, setData2] = useState([]);
    const [matrizCurricular, setMatrizCurricular] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCycle, setSelectedCycle] = useState('d2');
    const unidades = ['', 'Bento Ribeiro', 'Madureira', 'Santa Cruz', 'Cascadura', 'Taquara', 'Nilopolis', '', '', 'Seropédica', 'Barra da Tijuca', 'Campo Grande', 'Centro de Excelência', 'Mangueira', 'Maricá', 'Ilha do Governador', 'Freguesia', 'Recreio dos Bandeirantes'];
    const nomeUnidade = unidades[parseInt(un, 10)] || 'Unidade não identificada';

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await pega_ranking_soma_bolsa(un);
                const response2 = await pega_matriz_curricular();
                setData2(response);
                setMatrizCurricular(response2);
            } catch (error) {
                console.error("Erro ao buscar dados:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [un]);

    const processedData = useMemo(() => {
        if (!data2.length || !matrizCurricular.length) return [];
        // Filtra apenas alunos cuja matrícula começa com o código da unidade (un)
        const unidadePrefix = un.toString().padStart(2, '0');
        return data2
            .filter(item => item.mat && item.mat.startsWith(unidadePrefix))
            .map(item => {
                const _ser = item.tur.substring(2, 3);
                const _gra = item.tur.substring(0, 1);
                const serieName = convertSerie(`${_gra}-${_ser}`);

                const divisor = matrizCurricular.filter(mc => mc.ser === _ser && mc.gra === _gra && mc.sts === true && (parseInt(mc.dis) < 340 || parseInt(mc.dis) > 357)).length;

                if (divisor === 0) {
                    return { serie: serieName, d1: 0, d2: 0, d3: 0, d4: 0, d_anual: 0 };
                }
                const d1 = parseFloat(((parseFloat(item.c1) / (divisor * 25)) * 100).toFixed(2));
                const d2 = parseFloat(((parseFloat(item.c2) / (divisor * 25)) * 100).toFixed(2));
                const d3 = parseFloat(((parseFloat(item.c3) / (divisor * 25)) * 100).toFixed(2));
                const d4 = parseFloat(((parseFloat(item.c4) / (divisor * 25)) * 100).toFixed(2));
                const d_anual = parseFloat(((parseFloat(item.c1) + parseFloat(item.c2) + parseFloat(item.c3) + parseFloat(item.c4)) / (divisor * 100) * 100).toFixed(2));

                return { serie: serieName, d1, d2, d3, d4, d_anual };
            });
    }, [data2, matrizCurricular, un]);


    const summaryData = useMemo(() => {
        if (!processedData.length) return {};

        const ranges = {
            '0-25': (v) => v >= 0 && v < 25, '25-50': (v) => v >= 25 && v < 50,
            '50-70': (v) => v >= 50 && v < 70, '70-90': (v) => v >= 70 && v < 90,
            '90-100': (v) => v >= 90 && v <= 100,
        };
        const cycles = ['d1', 'd2', 'd3', 'd4', 'd_anual'];
        const summary = {};

        processedData.forEach(item => {
            if (!summary[item.serie]) {
                summary[item.serie] = {};
                cycles.forEach(cycle => {
                    summary[item.serie][cycle] = { '0-25': 0, '25-50': 0, '50-70': 0, '70-90': 0, '90-100': 0 };
                });
            }
            cycles.forEach(cycle => {
                for (const range in ranges) {
                    if (ranges[range](item[cycle])) {
                        summary[item.serie][cycle][range]++;
                    }
                }
            });
        });
        return summary;
    }, [processedData]);

    // Função para obter e ordenar as labels dos gráficos
    const getSortedLabels = (data) => {
        return Object.keys(data).sort((a, b) => {
            const orderA = serieSortOrder.get(a) || 99; // Fallback para séries não mapeadas
            const orderB = serieSortOrder.get(b) || 99;
            return orderA - orderB;
        });
    };

    // Preparação de dados para o GRÁFICO DE VALORES ABSOLUTOS
    const chartDataAbsolute = useMemo(() => {
        const labels = getSortedLabels(summaryData); // <-- NOVO: Usando a ordenação customizada
        const ranges = ['0-25', '25-50', '50-70', '70-90', '90-100'];
        const colors = ['#ff4d4f', '#ffc53d', '#ffec3d', '#52c41a', '#1890ff'];
        return {
            labels,
            datasets: ranges.map((range, index) => ({
                label: `${range}%`,
                data: labels.map(serie => summaryData[serie]?.[selectedCycle]?.[range] || 0),
                backgroundColor: colors[index],
            })),
        };
    }, [summaryData, selectedCycle]);


    // Preparação de dados para o GRÁFICO DE 100%
    const chartDataPercentage = useMemo(() => {
        const labels = getSortedLabels(summaryData); // <-- NOVO: Usando a ordenação customizada
        const ranges = ['0-25', '25-50', '50-70', '70-90', '90-100'];
        const colors = ['#ff4d4f', '#ffc53d', '#ffec3d', '#52c41a', '#1890ff'];

        const percentageData = labels.map(serie => {
            const cycleData = summaryData[serie]?.[selectedCycle];
            if (!cycleData) return ranges.map(() => 0);

            const total = ranges.reduce((sum, range) => sum + cycleData[range], 0);
            if (total === 0) return ranges.map(() => 0);

            return ranges.map(range => (cycleData[range] / total) * 100);
        });

        return {
            labels,
            datasets: ranges.map((range, index) => ({
                label: `${range}%`,
                data: percentageData.map(data => data[index]),
                backgroundColor: colors[index],
            })),
        };
    }, [summaryData, selectedCycle]);

    const chartOptions = (title, yAxisLabel, isPercentage = false) => ({
        plugins: {
            title: { display: true, text: title, font: { size: 16 } },
            legend: { position: 'top' },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) { label += ': '; }
                        if (context.parsed.y !== null) {
                            label += context.parsed.y.toFixed(2) + (isPercentage ? '%' : '');
                        }
                        return label;
                    }
                }
            }
        },
        responsive: true, maintainAspectRatio: false,
        scales: {
            x: { stacked: true, title: { display: true, text: 'Séries' } },
            y: {
                stacked: true,
                min: 0,
                max: isPercentage ? 100 : undefined,
                title: { display: true, text: yAxisLabel }
            },
        },
    });

    const infoPopover = (
        <Popover id="popover-basic">
            <Popover.Header as="h3">O que é Desempenho?</Popover.Header>
            <Popover.Body>
                <p>O <strong>Desempenho (D)</strong> mede o percentual de aproveitamento do aluno em relação à pontuação máxima possível em um ciclo ou no ano.</p>
                <p>A fórmula para o desempenho de um ciclo é:</p>
                <p><em>D = (Total de Pontos Obtidos / Pontuação Máxima Possível) × 100</em></p>
                <p>Onde a <strong>Pontuação Máxima Possível</strong> é calculada como <em>25 pontos multiplicados pelo número de disciplinas cursadas</em>.</p>
                <p>Para o desempenho anual, somam-se os pontos obtidos em todos os ciclos e divide-se pela pontuação máxima anual (que é 100 pontos multiplicados pelo número de disciplinas).</p>
                <p>O desempenho é expresso em porcentagem, variando de 0% a 100%.</p>
                <br />
                <strong>Exemplo por Ciclo:</strong>
                <p>Se um aluno cursa <strong>3 disciplinas</strong>, o máximo de pontos por ciclo é 3 x 25 = 75. Se ele obteve <strong>50 pontos</strong>, seu desempenho foi de (50 / 75) x 100 = 66,66%.</p>
                <strong>Exemplo Anual:</strong>
                <p>Considerando o mesmo aluno, se suas notas nos 4 ciclos foram 50, 60, 70 e 80, o somatório anual é <strong>260</strong>. O máximo anual seria 75 x 4 = 300 pontos. Seu desempenho anual foi de (260 / 300) x 100 = 86,66%.</p>
            </Popover.Body>
        </Popover>
    );

    return (
        <div className="DesempenhoPorSerie">
            <Container fluid className="mt-4">
                <h1 className="text-center mb-2">Análise de Desempenho por Série - {nomeUnidade}</h1>

                <Row className="justify-content-center mb-4">
                    <Col md={6} lg={4}>
                        <Form.Group controlId="cycleSelector">
                            <div className="d-flex align-items-center">
                                <Form.Label className="fw-bold mb-0 me-2">Selecione o Ciclo para Visualização</Form.Label>
                                <OverlayTrigger trigger="click" placement="right" overlay={infoPopover} rootClose>
                                    <Button variant="outline-info" size="sm" className="rounded-circle p-0" style={{ width: '24px', height: '24px' }}>
                                        ?
                                    </Button>
                                </OverlayTrigger>
                            </div>
                            <Form.Select value={selectedCycle} onChange={e => setSelectedCycle(e.target.value)}>
                                <option value="d1">Ciclo 1</option>
                                <option value="d2">Ciclo 2</option>
                                <option value="d3">Ciclo 3</option>
                                <option value="d4">Ciclo 4</option>
                                <option value="d_anual">Desempenho Anual</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                {loading ? (
                    <div className="text-center">Carregando...</div>
                ) : (
                    <Row>
                        <Col xl={6} className="mb-5">
                            <div style={{ height: '500px', position: 'relative' }}>
                                <Bar options={chartOptions('Distribuição por Quantidade de Alunos', 'Quantidade de Alunos')} data={chartDataAbsolute} />
                            </div>
                        </Col>
                        <Col xl={6}>
                            <div style={{ height: '500px', position: 'relative' }}>
                                <Bar options={chartOptions('Distribuição Percentual de Alunos por Faixa', 'Percentual de Alunos (%)', true)} data={chartDataPercentage} />
                            </div>
                        </Col>
                    </Row>
                )}
            </Container>
        </div>
    );
};

export default DesempenhoPorSerie;